import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import OpenSeadragon from 'openseadragon'
import { pageImageUrl, type Region } from '../../lib/api'
import { regionStyle, typeOf } from '../Page'
import { Glyph } from './Glyph'
import { MIN_TAB_HEIGHT, bboxToIiif, toPt, type PageGeometry, type RegionItem } from './model'
import type { PageHit } from '../../lib/api'
import type { TypeKey } from '../Page'

/**
 * The page well.
 *
 * Near-black in both themes, because the paper of the scan has to be the
 * brightest thing on screen and a page image is never dimmed, inverted or
 * filtered. It is evidence.
 *
 * The overlay is drawn as ordinary elements positioned from the viewport
 * rather than as scaled children of the image, so a 1.5px outline stays 1.5px
 * at every zoom. Above about 3% of page height a region carries its
 * three-letter tab; below that the tab is suppressed rather than shrunk,
 * because a code drawn at 5px is not a label, it is a smudge on the newsprint.
 */

interface Transform {
  ox: number
  oy: number
  scale: number
}

export interface WellHandle {
  viewer: OpenSeadragon.Viewer | null
  zoomBy: (factor: number) => void
  fit: () => void
  fitTo: (bbox: Region, padding?: number) => void
}

export function PageWell({
  pageId,
  geometry,
  regions,
  visibleTypes,
  overlayOn,
  selectedKey,
  selectionBox,
  onSelect,
  hits,
  activeHit,
  onActivateHit,
  selecting,
  onMarquee,
  pendingBox = null,
  onPointer,
  onTransform,
  onReady,
  labelsOnSelectionOnly = false,
  touch = false,
  showNavigator = true,
  showControls = true,
  className = '',
  children,
}: {
  pageId: string
  geometry: PageGeometry
  regions: RegionItem[]
  visibleTypes: Set<TypeKey>
  overlayOn: boolean
  selectedKey: string | null
  selectionBox: Region | null
  onSelect: (region: RegionItem | null) => void
  hits: PageHit[]
  activeHit: number
  onActivateHit?: (index: number) => void
  selecting: boolean
  onMarquee: (bbox: Region) => void
  /** A drawn box still waiting on its clipping panel: kept on the page. */
  pendingBox?: Region | null
  onPointer?: (point: { x: number; y: number } | null) => void
  onTransform?: (zoomPercent: number) => void
  onReady?: (handle: WellHandle) => void
  labelsOnSelectionOnly?: boolean
  touch?: boolean
  showNavigator?: boolean
  showControls?: boolean
  className?: string
  children?: ReactNode
}) {
  /* The marquee's listeners must not be torn down by a parent re-render: a
     caller who passes an inline handler would otherwise cancel the drag the
     first time the drag itself caused a render. */
  const marquee = useRef(onMarquee)
  marquee.current = onMarquee

  const host = useRef<HTMLDivElement>(null)
  const viewer = useRef<OpenSeadragon.Viewer | null>(null)
  const layer = useRef<HTMLDivElement>(null)
  const [transform, setTransform] = useState<Transform>({ ox: 0, oy: 0, scale: 0 })
  const [hovered, setHovered] = useState<string | null>(null)
  const [focusIndex, setFocusIndex] = useState(0)
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const [tiles, setTiles] = useState<{ loaded: number; total: number } | null>(null)
  const [source, setSource] = useState<string | null>(null)
  const [bytes, setBytes] = useState<{ loaded: number; total: number } | null>({
    loaded: 0,
    total: 0,
  })
  const [bounds, setBounds] = useState<{ x: number; y: number; w: number; h: number }>({
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  })

  /* No spinner without a count. The page is delivered as one full raster
     today, so the count is the bytes of that raster rather than tiles of a
     pyramid that does not exist yet; reading it through the stream is what
     makes the figure real rather than an animation standing in for one. */
  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const controller = new AbortController()
    setSource(null)
    setBytes({ loaded: 0, total: 0 })

    const load = async () => {
      try {
        const response = await fetch(pageImageUrl(pageId), {
          credentials: 'same-origin',
          signal: controller.signal,
        })
        if (!response.ok || !response.body) throw new Error(String(response.status))
        const total = Number(response.headers.get('content-length') ?? 0)
        const reader = response.body.getReader()
        const chunks: BlobPart[] = []
        let loaded = 0
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          if (value) {
            chunks.push(value)
            loaded += value.length
            if (!cancelled) setBytes({ loaded, total })
          }
        }
        const blob = new Blob(chunks, {
          type: response.headers.get('content-type') ?? 'image/jpeg',
        })
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) {
          setSource(objectUrl)
          setBytes(null)
        }
      } catch {
        // A licence denial or a dropped connection: hand the URL straight to
        // the viewer, which reports its own failure rather than hiding one.
        if (!cancelled) {
          setSource(pageImageUrl(pageId))
          setBytes(null)
        }
      }
    }
    void load()

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [pageId])

  /* A fit asked for before the image has opened is kept and applied on
     `open`. The viewer is created only once the raster has downloaded, so the
     first thing a search result asks for arrives before there is anything to
     ask; without this it was silently dropped and the page opened whole. */
  const pendingFit = useRef<OpenSeadragon.Rect | null>(null)

  // --- the viewer ----------------------------------------------------------
  useEffect(() => {
    if (!host.current || !source) return
    const instance = OpenSeadragon({
      element: host.current,
      tileSources: { type: 'image', url: source },
      showNavigationControl: false,
      showNavigator: false,
      gestureSettingsTouch: { pinchToZoom: true, flickEnabled: true, dblClickToZoom: true },
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true },
      maxZoomPixelRatio: 4,
      minZoomImageRatio: 0.5,
      visibilityRatio: 0.85,
      constrainDuringPan: true,
      animationTime: 0.6,
      springStiffness: 7,
      immediateRender: true,
    })
    viewer.current = instance
    instance.addHandler('open', () => {
      const rect = pendingFit.current
      if (!rect) return
      pendingFit.current = null
      // The viewer applies its own home bounds while opening; a fit issued in
      // the same tick is overwritten, so it waits one.
      window.setTimeout(() => instance.viewport.fitBounds(rect, false), 0)
    })
    return () => {
      instance.destroy()
      viewer.current = null
    }
  }, [pageId, source])

  // --- viewport to screen --------------------------------------------------
  useEffect(() => {
    const instance = viewer.current
    if (!instance) return
    const update = () => {
      if (instance.world.getItemCount() === 0) return
      const viewport = instance.viewport
      const origin = viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(0, 0))
      const unit = viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(1000, 0))
      const scale = (unit.x - origin.x) / 1000
      setTransform({ ox: origin.x, oy: origin.y, scale })
      const box = viewport.getBounds(true)
      const image = instance.world.getItemAt(0)
      const size = image.getContentSize()
      const ratio = size.y / size.x
      setBounds({ x: box.x, y: box.y / ratio, w: box.width, h: box.height / ratio })
    }
    instance.addHandler('update-viewport', update)
    instance.addHandler('open', update)
    instance.addHandler('resize', update)
    update()
    return () => {
      instance.removeHandler('update-viewport', update)
      instance.removeHandler('open', update)
      instance.removeHandler('resize', update)
    }
  }, [pageId, source])

  useEffect(() => {
    if (transform.scale > 0) onTransform?.(transform.scale * 100)
  }, [transform.scale, onTransform])

  /* No spinner without a count: the panel reports tiles arrived against tiles
     expected, and disappears when the delivery is complete. */
  useEffect(() => {
    const instance = viewer.current
    if (!instance) return
    let loaded = 0
    const recount = () => {
      const image = instance.world.getItemCount() ? instance.world.getItemAt(0) : null
      if (!image) return
      const source = image.source as {
        maxLevel?: number
        getNumTiles?: (level: number) => number | { x: number; y: number }
      }
      const grid = source.getNumTiles ? source.getNumTiles(source.maxLevel ?? 0) : 1
      const total = Math.max(1, typeof grid === 'number' ? grid : (grid.x ?? 1) * (grid.y ?? 1))
      setTiles(image.getFullyLoaded() ? null : { loaded, total })
    }
    const onTile = () => {
      loaded += 1
      recount()
    }
    const onOpen = () => {
      recount()
      const image = instance.world.getItemCount() ? instance.world.getItemAt(0) : null
      image?.addHandler('fully-loaded-change', () => setTiles(null))
    }
    instance.addHandler('tile-loaded', onTile)
    instance.addHandler('open', onOpen)
    return () => {
      instance.removeHandler('tile-loaded', onTile)
      instance.removeHandler('open', onOpen)
    }
  }, [pageId, source])

  const fitTo = useCallback(
    (bbox: Region, padding = 1.25) => {
      if (!geometry.widthPx) return
      const scale = 1 / geometry.widthPx
      // Padding grows the box about its own centre. Scaling it instead (OSD's
      // Rect.times) moves the origin as well, which walks the region off the
      // sheet at anything above a small factor.
      const width = (bbox[2] - bbox[0]) * scale
      const height = (bbox[3] - bbox[1]) * scale
      const rect = new OpenSeadragon.Rect(
        bbox[0] * scale - (width * (padding - 1)) / 2,
        bbox[1] * scale - (height * (padding - 1)) / 2,
        width * padding,
        height * padding,
      )
      const instance = viewer.current
      if (instance && instance.world.getItemCount() > 0) {
        pendingFit.current = null
        instance.viewport.fitBounds(rect, false)
      } else {
        pendingFit.current = rect
      }
    },
    [geometry.widthPx],
  )

  useEffect(() => {
    if (!onReady) return
    onReady({
      viewer: viewer.current,
      zoomBy: (factor) => viewer.current?.viewport.zoomBy(factor).applyConstraints(),
      fit: () => viewer.current?.viewport.goHome(),
      fitTo,
    })
  }, [onReady, fitTo, pageId, source])

  // --- marquee -------------------------------------------------------------
  const toImage = useCallback((clientX: number, clientY: number) => {
    const instance = viewer.current
    const element = layer.current
    if (!instance || !element || instance.world.getItemCount() === 0) return null
    const rect = element.getBoundingClientRect()
    const point = instance.viewport.viewerElementToImageCoordinates(
      new OpenSeadragon.Point(clientX - rect.left, clientY - rect.top),
    )
    return { x: point.x, y: point.y }
  }, [])

  useEffect(() => {
    const instance = viewer.current
    const element = layer.current
    if (!instance || !element || !selecting) return
    instance.setMouseNavEnabled(false)
    let origin: { x: number; y: number } | null = null

    const down = (event: PointerEvent) => {
      const point = toImage(event.clientX, event.clientY)
      if (!point) return
      origin = point
      setDrag({ x0: point.x, y0: point.y, x1: point.x, y1: point.y })
      element.setPointerCapture(event.pointerId)
    }
    const move = (event: PointerEvent) => {
      if (!origin) return
      const point = toImage(event.clientX, event.clientY)
      if (!point) return
      setDrag({ x0: origin.x, y0: origin.y, x1: point.x, y1: point.y })
    }
    const up = () => {
      if (!origin) return
      setDrag((current) => {
        if (current) {
          const bbox: Region = [
            Math.min(current.x0, current.x1),
            Math.min(current.y0, current.y1),
            Math.max(current.x0, current.x1),
            Math.max(current.y0, current.y1),
          ]
          if (bbox[2] - bbox[0] > 8 && bbox[3] - bbox[1] > 8) marquee.current(bbox)
        }
        return null
      })
      origin = null
    }

    element.addEventListener('pointerdown', down)
    element.addEventListener('pointermove', move)
    element.addEventListener('pointerup', up)
    return () => {
      instance.setMouseNavEnabled(true)
      element.removeEventListener('pointerdown', down)
      element.removeEventListener('pointermove', move)
      element.removeEventListener('pointerup', up)
      setDrag(null)
    }
  }, [selecting, toImage])

  // --- the cursor readout --------------------------------------------------
  const trackPointer = (event: React.PointerEvent) => {
    if (!onPointer) return
    const point = toImage(event.clientX, event.clientY)
    onPointer(point)
  }

  const visible = regions.filter((region) => visibleTypes.has(region.typeKey))
  const { ox, oy, scale } = transform
  const place = (bbox: Region) => ({
    left: ox + bbox[0] * scale,
    top: oy + bbox[1] * scale,
    width: Math.max(1, (bbox[2] - bbox[0]) * scale),
    height: Math.max(1, (bbox[3] - bbox[1]) * scale),
  })

  /* At whole-page zoom a word box is a few pixels wide. A search mark keeps a
     floor, grown about the box's centre so the word stays under it, so the
     seven hits the toolbar counts can be found on the sheet. */
  const markerBox = (box: { left: number; top: number; width: number; height: number }) => {
    const width = Math.max(box.width, 14)
    const height = Math.max(box.height, 10)
    return {
      left: box.left - (width - box.width) / 2,
      top: box.top - (height - box.height) / 2,
      width,
      height,
    }
  }

  const dragBox: Region | null = drag
    ? [
        Math.min(drag.x0, drag.x1),
        Math.min(drag.y0, drag.y1),
        Math.max(drag.x0, drag.x1),
        Math.max(drag.y0, drag.y1),
      ]
    : pendingBox

  const onLayerKey = (event: React.KeyboardEvent) => {
    if (!visible.length) return
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      const next = Math.min(visible.length - 1, focusIndex + 1)
      setFocusIndex(next)
      onSelect(visible[next])
      fitTo(visible[next].bbox, 1.4)
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      const next = Math.max(0, focusIndex - 1)
      setFocusIndex(next)
      onSelect(visible[next])
      fitTo(visible[next].bbox, 1.4)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(visible[focusIndex] ?? null)
    }
  }

  return (
    <div
      className={`relative min-h-0 min-w-0 flex-1 overflow-hidden bg-well ${className}`}
      onPointerMove={trackPointer}
      onPointerLeave={() => onPointer?.(null)}
    >
      <div ref={host} className="absolute inset-0" />

      <div
        ref={layer}
        data-region-layer
        tabIndex={0}
        role="application"
        aria-label={`Region layer: ${visible.length} regions in reading order. Arrow keys move between them.`}
        onKeyDown={onLayerKey}
        className="absolute inset-0"
        style={{ cursor: selecting ? 'crosshair' : 'default', pointerEvents: selecting ? 'auto' : 'none' }}
      >
        {overlayOn &&
          scale > 0 &&
          visible.map((region, index) => {
            const chosen = region.key === selectedKey
            const state = chosen ? 'selected' : hovered === region.key ? 'hover' : 'rest'
            const box = place(region.bbox)
            const tall = (region.bbox[3] - region.bbox[1]) / (geometry.heightPx || 1)
            const showTab =
              tall > MIN_TAB_HEIGHT && box.height > 14 && (!labelsOnSelectionOnly || chosen)
            const type = typeOf(region.rawType)
            return (
              <div
                key={region.key}
                style={{
                  position: 'absolute',
                  ...box,
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  pointerEvents: selecting ? 'none' : 'auto',
                  ...regionStyle(region.rawType, state),
                  ...(index === focusIndex && !chosen ? {} : {}),
                }}
                onMouseEnter={() => !touch && setHovered(region.key)}
                onMouseLeave={() => !touch && setHovered(null)}
              >
                <button
                  type="button"
                  aria-label={`${type.name}, region ${region.ordinal}${
                    region.title ? `: ${region.title}` : ''
                  }`}
                  aria-pressed={chosen}
                  onClick={() => {
                    setFocusIndex(index)
                    onSelect(region)
                  }}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: touch ? `max(100%, 44px)` : '100%',
                    height: touch ? `max(100%, 44px)` : '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                />
                {showTab && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -1,
                      left: -1,
                      padding: '0 3px',
                      fontFamily: 'var(--jl-mono)',
                      fontSize: 8,
                      lineHeight: 1.5,
                      background: chosen ? 'var(--jl-accent)' : type.colour,
                      color: 'var(--color-bg)',
                      borderRadius: '1px 0 1px 0',
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {chosen ? `R${String(region.ordinal).padStart(2, '0')}` : type.code}
                  </span>
                )}
                {hovered === region.key && !chosen && !touch && box.height > 26 && (
                  <span
                    style={{
                      position: 'absolute',
                      left: 1,
                      bottom: 1,
                      whiteSpace: 'nowrap',
                      background: type.colour,
                      color: 'var(--color-bg)',
                      fontFamily: 'var(--jl-mono)',
                      fontSize: 8.5,
                      padding: '1px 4px',
                      borderRadius: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    {`${type.code} · ${Math.round(region.bbox[2] - region.bbox[0])}×${Math.round(
                      region.bbox[3] - region.bbox[1],
                    )} px · ord ${String(region.ordinal).padStart(2, '0')}`}
                  </span>
                )}
              </div>
            )
          })}

        {/* The search matches, in the accent: one of its four reserved jobs.
            Drawn on the clean page too: they are what the reader searched for. */}
        {scale > 0 &&
          hits.map((hit, index) => {
            const box = markerBox(place(hit.bbox))
            const on = index === activeHit
            return (
              <button
                key={`${hit.block_id}-${index}`}
                type="button"
                aria-label={`Search match ${index + 1} of ${hits.length}: ${hit.text}`}
                onClick={() => onActivateHit?.(index)}
                style={{
                  position: 'absolute',
                  ...box,
                  boxSizing: 'border-box',
                  padding: 0,
                  border: `${on ? 2 : 1.5}px solid var(--jl-accent)`,
                  background: on
                    ? 'color-mix(in srgb, var(--jl-accent) 16%, transparent)'
                    : 'color-mix(in srgb, var(--jl-accent) 11%, transparent)',
                  // A paper halo lifts the mark off dense newsprint at any zoom.
                  boxShadow: '0 0 0 1px color-mix(in srgb, var(--color-bg) 85%, transparent)',
                  borderRadius: 2,
                  pointerEvents: selecting ? 'none' : 'auto',
                  cursor: 'pointer',
                }}
              />
            )
          })}

        {/* A region carried in on a permalink, before it is matched to a unit. */}
        {overlayOn && scale > 0 && selectionBox && !selectedKey && (
          <div
            style={{
              position: 'absolute',
              ...place(selectionBox),
              boxSizing: 'border-box',
              ...regionStyle('article', 'selected'),
              borderRadius: 3,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* The marquee dims everything outside it and leaves the crop
            undimmed, so the researcher is framing the pixels they will cite. */}
        {dragBox && scale > 0 && (
          <div
            style={{
              position: 'absolute',
              ...place(dragBox),
              boxSizing: 'border-box',
              border: '1.5px dashed var(--jl-accent-dark)',
              boxShadow: '0 0 0 9999px color-mix(in srgb, var(--jl-well) 58%, transparent)',
              pointerEvents: 'none',
            }}
          >
            {[
              { left: -4, top: -4 },
              { right: -4, top: -4 },
              { left: -4, bottom: -4 },
              { right: -4, bottom: -4 },
            ].map((corner, index) => (
              <span
                key={index}
                style={{
                  position: 'absolute',
                  ...corner,
                  width: 7,
                  height: 7,
                  background: 'var(--jl-accent-dark)',
                }}
              />
            ))}
            <span
              style={{
                position: 'absolute',
                left: 3,
                bottom: 3,
                whiteSpace: 'nowrap',
                fontFamily: 'var(--jl-mono)',
                fontSize: 9,
                color: 'color-mix(in srgb, white 88%, var(--jl-well))',
                background: 'color-mix(in srgb, var(--jl-well) 90%, transparent)',
                padding: '1px 4px',
              }}
            >
              {`${Math.round(dragBox[2] - dragBox[0])} × ${Math.round(
                dragBox[3] - dragBox[1],
              )} px · ${toPt(dragBox[2] - dragBox[0], geometry.ppi).toFixed(1)} × ${toPt(
                dragBox[3] - dragBox[1],
                geometry.ppi,
              ).toFixed(1)} pt`}
            </span>
          </div>
        )}
      </div>

      {selecting && !drag && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2">
          <WellChip>Drag a box around the passage to clip it</WellChip>
        </div>
      )}

      {(bytes || tiles) && (
        <div
          className="absolute bottom-3 left-3 right-3 z-20 rounded-[--radius-md] px-3 py-2.5"
          style={{
            background: 'color-mix(in srgb, var(--jl-well) 86%, transparent)',
            border: '1px solid color-mix(in srgb, white 22%, var(--jl-well))',
            maxWidth: 320,
          }}
        >
          <div
            className="mb-1.5 flex justify-between font-mono text-[10px]"
            style={{ color: 'color-mix(in srgb, white 88%, var(--jl-well))' }}
          >
            <span>
              {tiles
                ? `${tiles.loaded} OF ${tiles.total} TILES`
                : bytes?.total
                  ? `${Math.round(bytes.loaded / 1024)} OF ${Math.round(bytes.total / 1024)} KB`
                  : `${Math.round((bytes?.loaded ?? 0) / 1024)} KB RECEIVED`}
            </span>
            <span style={{ color: 'color-mix(in srgb, white 58%, var(--jl-well))' }}>
              {`${geometry.widthPx} × ${geometry.heightPx} PX · ${geometry.ppi} PPI`}
            </span>
          </div>
          <div
            className="h-1 overflow-hidden rounded-[2px]"
            style={{ background: 'color-mix(in srgb, white 22%, var(--jl-well))' }}
          >
            <div
              className="h-full"
              style={{
                width: `${
                  tiles
                    ? Math.min(100, (tiles.loaded / tiles.total) * 100)
                    : bytes?.total
                      ? Math.min(100, (bytes.loaded / bytes.total) * 100)
                      : 4
                }%`,
                background: 'var(--jl-accent-dark)',
              }}
            />
          </div>
        </div>
      )}

      {showControls && (
        <div className="absolute bottom-3 right-3 z-10 flex flex-col items-end gap-1.5">
          <div className="flex gap-1.5">
            <WellButton
              label="Fit the whole page"
              onClick={() => viewer.current?.viewport.goHome()}
            >
              <Glyph name="fit" />
            </WellButton>
            <WellButton
              label="Full screen"
              onClick={() => {
                const instance = viewer.current
                if (instance) instance.setFullScreen(!instance.isFullPage())
              }}
            >
              <Glyph name="expand" />
            </WellButton>
          </div>
          {showNavigator && (
            <div
              className="relative"
              style={{
                width: 104,
                height: 212,
                padding: 4,
                borderRadius: 'var(--radius-md)',
                background: 'color-mix(in srgb, var(--jl-well) 86%, transparent)',
                border: '1px solid color-mix(in srgb, white 22%, var(--jl-well))',
              }}
              aria-hidden="true"
            >
              <img
                src={pageImageUrl(pageId, true)}
                alt=""
                className="block h-full w-full object-contain opacity-50"
              />
              <div
                style={{
                  position: 'absolute',
                  left: `calc(4px + ${Math.max(0, Math.min(1, bounds.x)) * 96}px)`,
                  top: `calc(4px + ${Math.max(0, Math.min(1, bounds.y)) * 204}px)`,
                  width: `${Math.min(1, bounds.w) * 96}px`,
                  height: `${Math.min(1, bounds.h) * 204}px`,
                  border: '1.5px solid var(--jl-accent-dark)',
                  background: 'color-mix(in srgb, var(--jl-accent-dark) 14%, transparent)',
                }}
              />
            </div>
          )}
        </div>
      )}

      {children}
    </div>
  )
}

/** A label over the well. Light on dark in both themes: the well never inverts. */
export function WellChip({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-[--radius-md] px-3 py-1.5 text-[13px]"
      style={{
        background: 'color-mix(in srgb, var(--jl-well) 86%, transparent)',
        border: '1px solid color-mix(in srgb, white 22%, var(--jl-well))',
        color: 'color-mix(in srgb, white 88%, var(--jl-well))',
      }}
    >
      {children}
    </span>
  )
}

export function WellButton({
  label,
  onClick,
  size = 28,
  children,
}: {
  label: string
  onClick: () => void
  size?: number
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="flex items-center justify-center rounded-[--radius-md]"
      style={{
        width: size,
        height: size,
        background: 'color-mix(in srgb, var(--jl-well) 86%, transparent)',
        border: '1px solid color-mix(in srgb, white 22%, var(--jl-well))',
        color: 'color-mix(in srgb, white 88%, var(--jl-well))',
      }}
    >
      {children}
    </button>
  )
}

/** The IIIF region string, so a caller can print the address it just drew. */
export const regionAddress = bboxToIiif
